import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FormStatus, ValidationContext } from '~/components/hoc/withValidation';
import Header from '~/components/shared/Header/Header';
import Navbar from '~/components/shared/Navbar/Navbar';
import { marketAndLanguagePerReferrerUrl } from '~/constants/login';
import { FlowTypes, StepStatus } from '~/enums';
import { getCookieValue } from '~/helpers/utils';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { RootState } from '~/store';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import {
  clearLoanApplicationState,
  goToPreviousStep,
  updateForm,
} from '~/store/slices/loanApplication.slice';
import { StandardPageContainer } from '~/styles/BaseStyledComponents';
import useStepConfig from './useStepConfig';

const Steps = () => {
  const dispatch = useAppDispatch();
  const validationContext = useContext(ValidationContext);
  const history = useHistory();

  const publicToken = getCookieValue('publicToken');

  const {
    loanApplication: { form, flowType, currentStepNumber, currentStepName },
  } = useSelector((state: RootState) => state);

  const { stepConfig, currentStepConfig } = useStepConfig();

  const onBackClick = () => {
    dispatch(pushToGtmLoanAction({ actionName: 'back_button_pressed' }));
    const referrerUrl = document.referrer && new URL(document.referrer);
    const shouldGoBackInBrowserHistory =
      currentStepNumber === 1 &&
      referrerUrl &&
      referrerUrl?.origin &&
      marketAndLanguagePerReferrerUrl[referrerUrl.origin];

    if (shouldGoBackInBrowserHistory) {
      history.goBack();
    } else {
      dispatch(goToPreviousStep());
      dispatch(pushToGtmLoanAction({ stepStatus: StepStatus.Edit }));
    }
  };

  useEffect(() => {
    if (!form.sessionId) {
      const sessionId = `${Math.random()}${Date.now()}`.replace('0.', '');
      dispatch(updateForm({ sessionId }));
    }
  }, [dispatch, form.sessionId]);

  useEffect(() => {
    if (currentStepName) {
      dispatch(pushToGtmLoanAction({ stepStatus: StepStatus.Started }));
      validationContext.setFormStatus(FormStatus.IDLE);
    }
  }, [currentStepName]);

  useEffect(() => {
    const userIsPastFirstStep = currentStepNumber > 1;
    if (flowType === FlowTypes.Manual && !publicToken && userIsPastFirstStep) {
      dispatch(clearLoanApplicationState());
    }
  }, [currentStepNumber, dispatch, flowType, publicToken]);

  if (!currentStepConfig) {
    throw new Error(
      `Current step config is undefined. Ensure that the step config is properly defined for the current step (${currentStepNumber}).`
    );
  }

  const StepComponent = currentStepConfig.component;
  const { hideNavbar, hideInProgressStepper } = currentStepConfig;

  return (
    <StandardPageContainer>
      <Header />
      {!hideNavbar && (
        <Navbar
          onBackClick={onBackClick}
          showBackButton
          progressStepperDots={
            hideInProgressStepper
              ? undefined
              : {
                  steps: stepConfig.map((step) => ({
                    name: step.name,
                    hideInProgressStepper: step.hideInProgressStepper,
                  })),
                  currentStep: currentStepNumber,
                }
          }
        />
      )}

      <StepComponent />
    </StandardPageContainer>
  );
};

export default Steps;
